import { Input } from 'app/components/ui/input'
import { treatmentCategories } from 'lib/cdtCodeDefaults'
import {
  guidedValue,
  TREATMENT_MILESTONES,
  type TreatmentMilestone,
  withGuidedValue,
  withoutOtherMomentRows,
} from 'app/lib/workflows/treatmentTrigger'
import type { TriggerNodeData } from 'app/types/workflows'
import { ChoiceSelect, Field, MultiSelect } from './fields'
import type { SetFn } from './panelShared'
import { Section } from './Section'

const MILESTONE_CHOICES = TREATMENT_MILESTONES.map(({ value, label }) => ({ value, label }))
const CATEGORY_CHOICES = treatmentCategories.map((category) => ({ value: category, label: category }))
const WHEN_CHOICES = [
  { value: `each`, label: `Each time work is completed` },
  { value: `finished`, label: `Only when the whole plan is finished` },
]

/** Each answer is the trigger's treatment choice or an ordinary change row, so the condition
 *  editor below shows the same thing. Nothing is prefilled. */
export function TreatmentTriggerFields({ data, set }: { data: TriggerNodeData; set: SetFn }) {
  const treatment = data.treatment ?? {}
  const milestone = treatment.milestone
  const conditions = data.conditions ?? []

  return (
    <Section title="Treatment">
      <Field label="What happened" error={milestone ? undefined : `Choose what happened to the treatment plan.`}>
        <ChoiceSelect
          value={milestone ?? ``}
          choices={MILESTONE_CHOICES}
          placeholder="Select a moment"
          invalid={!milestone}
          ariaLabel="What happened"
          onChange={(value) =>
            set({
              treatment: { ...treatment, milestone: value as TreatmentMilestone },
              conditions: withoutOtherMomentRows(conditions, value as TreatmentMilestone),
            })
          }
        />
      </Field>
      {milestone && (
        <>
          <Field
            label="Which treatment"
            info="Leave empty for any treatment. Categories follow the ADA's code ranges, so a practice's own variant of a code counts too."
          >
            <MultiSelect
              choices={CATEGORY_CHOICES}
              selected={treatment.categories ?? []}
              placeholder="Any treatment"
              onChange={(categories) =>
                set({ treatment: { ...treatment, categories: categories.length ? categories : undefined } })
              }
            />
          </Field>
          <Field label="Procedure codes" info="Exact codes, separated by commas. Leave empty for any code.">
            <Input
              key={(treatment.codes ?? []).join(`,`)}
              defaultValue={(treatment.codes ?? []).join(`, `)}
              placeholder="Any code"
              aria-label="Procedure codes"
              onBlur={(event) => {
                const codes = event.target.value
                  .split(/[\s,]+/)
                  .map((code) => code.trim().toUpperCase())
                  .filter(Boolean)
                set({ treatment: { ...treatment, codes: codes.length ? codes : undefined } })
              }}
            />
          </Field>
        </>
      )}
      {milestone === `presented` && (
        <Field label="What size" info="The value of the treatment just added. Leave empty for any amount.">
          <Input
            type="number"
            min={0}
            value={guidedValue(conditions, `changedValue`) ?? ``}
            placeholder="Any amount"
            aria-label="Worth more than"
            onChange={(event) =>
              event.target.value.startsWith(`-`)
                ? undefined
                : set({ conditions: withGuidedValue(conditions, `changedValue`, `greater_than`, event.target.value) })
            }
          />
        </Field>
      )}
      {milestone === `broken_booking` && (
        <Field
          label="Still unbooked"
          info="The plan's unbooked value after the booking broke. Leave empty for any amount."
        >
          <Input
            type="number"
            min={0}
            value={guidedValue(conditions, `unbookedValueAfter`) ?? ``}
            placeholder="Any amount"
            aria-label="Unbooked value more than"
            onChange={(event) =>
              event.target.value.startsWith(`-`)
                ? undefined
                : set({
                    conditions: withGuidedValue(conditions, `unbookedValueAfter`, `greater_than`, event.target.value),
                  })
            }
          />
        </Field>
      )}
      {milestone === `completed` && (
        <Field label="When">
          <ChoiceSelect
            value={guidedValue(conditions, `pendingLinesAfter`) === `0` ? `finished` : `each`}
            choices={WHEN_CHOICES}
            ariaLabel="When"
            onChange={(value) =>
              set({
                conditions: withGuidedValue(
                  conditions,
                  `pendingLinesAfter`,
                  `is`,
                  value === `finished` ? `0` : undefined,
                ),
              })
            }
          />
        </Field>
      )}
    </Section>
  )
}
